import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import MuiLink from '@material-ui/core/Link';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Snackbar from '@material-ui/core/Snackbar';
import ArrowBack from '@material-ui/icons/ArrowBack';
import Delete from '@material-ui/icons/Delete';
import FileCopy from '@material-ui/icons/FileCopy';
import Save from '@material-ui/icons/Save';
import Alert from "@material-ui/lab/Alert";
import { useSnackbar } from "notistack";
import React, { useState } from "react";
import { Link, useHistory } from "react-router-dom";

import { Collections } from "Collections";
import { FullscreenLoading } from "components/FullscreenLoading";
import { useOrgIdLocalStorage } from 'components/OrgProvider';
import { PageNavigation } from "components/PageNavigation";
import { FirebaseUtils } from "firebaseUtils/FirebaseUtils";
import { AnalyticsEvent } from "models/AnalyticsEvent";
import { AppUser } from "models/AppUser";
import { Nullable } from 'models/Nullable';
import { OrgUser } from "models/OrgUser";
import { WithoutId } from "models/WithoutId";
import { Paths } from "Paths";

import { Entry } from "./ListScreen";
import { Typography } from '@material-ui/core';

export type FormGetter = (props: { save: () => void }) => React.ReactNode

export type EditProps = {
    clearForm: () => void
    getForm: FormGetter
    formIsValid: boolean
}

type UpdateProps<T extends Entry> = {
    id: string
    updates: Nullable<Partial<WithoutId<T>>>
    hasChanges?: boolean,
    updateErrorEvent: AnalyticsEvent
    updateEvent: AnalyticsEvent
    updateContextEvent: AnalyticsEvent
    editProps: EditProps
}

type CreateProps<T extends Entry> = {
    id?: string
    data: Nullable<WithoutId<T>>
    createErrorEvent: AnalyticsEvent
    createEvent: AnalyticsEvent
    createContextEvent: AnalyticsEvent
    goToOnCreated?: boolean
    createNewTitle: string
    editProps: EditProps
}

type Props<T extends Entry> = {
    requireOrgId?: boolean,
    collectionName: Collections
    title: string
    listPath: Paths
    notFoundText: string
    singularText: string
    children?: React.ReactNode
    loading?: boolean
    saved?: T
    hideNavigation?: boolean
    fabSaveButton?: boolean
    persist?: boolean
    orgUser: Nullable<OrgUser>
    appUser: Nullable<AppUser>
    altCreateNewProps?: {
        createNewText?: string
        createNewPath?: Paths
    }
    copyProps?: {
        onCopy?: (newDocId: string) => void
        createEvent: AnalyticsEvent
        copyContextEvent: AnalyticsEvent
        copyErrorEvent: AnalyticsEvent
    }
    deleteProps?: {
        deleteErrorEvent: AnalyticsEvent
        deleteEvent: AnalyticsEvent
        deleteContextEvent: AnalyticsEvent
    }
    createProps?: CreateProps<T>
    updateProps?: UpdateProps<T>
    canEdit: boolean
    canCreate: boolean
    canDelete: boolean
}

function saveButtons(props: EditProps, id: string | undefined, save: () => void, singularText: string, history: ReturnType<typeof useHistory>, listPath: string, canSave: boolean, appUser: Nullable<AppUser>, orgUser: Nullable<OrgUser>, lastCreated: string | undefined, canEdit: boolean) {
    return (<>
        {props.getForm({ save })}
        {canEdit ? (
            <ListItem>
                <Box mr={1} hidden={!lastCreated || lastCreated === id} width="100%">
                    <Button
                        variant="contained"
                        color="secondary"
                        fullWidth
                        onClick={() => history.push(listPath + '/' + lastCreated)}
                    >Gå til forrige</Button>
                    <div></div>
                </Box>
                {(appUser && orgUser) ? (
                    <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        disabled={!props.formIsValid || !canSave}
                        onClick={save}
                    >Lagre {singularText}</Button>
                ) : null}
            </ListItem>
        ) : null}
    </>)
}

export function ListEntryScreen<T extends Entry>({
    collectionName,
    title,
    listPath,
    hideNavigation,
    notFoundText,
    singularText,
    children,
    loading,
    saved,
    fabSaveButton,
    persist,
    orgUser,
    appUser,
    copyProps,
    deleteProps,
    createProps,
    updateProps,
    canEdit,
    canCreate,
    canDelete,
    altCreateNewProps,
    requireOrgId,
}: Props<T>) {
    const [success, setSuccess] = useState('');
    const [lastCreated, setLastCreated] = useState('');
    const { enqueueSnackbar } = useSnackbar()
    const [orgId] = useOrgIdLocalStorage()

    const history = useHistory();

    const [snacbar, setSnacbar] = useState('');

    const viewMode = !updateProps || !createProps

    function save() {
        if (orgUser && appUser && !saved && createProps && createProps.editProps.formIsValid && createProps.data) {
            const newId = FirebaseUtils.createDocument<T>(
                !!persist,
                collectionName,
                null,
                appUser,
                orgUser,
                createProps.createEvent,
                createProps.createContextEvent,
                createProps.createErrorEvent,
                enqueueSnackbar,
                'Vellykket!',
                createProps.data,
            )

            setSuccess('Vellykket!')
            createProps.editProps.clearForm()
            setLastCreated(newId)
            if (createProps.goToOnCreated) {
                history.push(listPath + '/' + newId)
            }
        } else if (orgUser && appUser && saved && updateProps && updateProps.editProps.formIsValid && updateProps.updates) {
            FirebaseUtils.updateDocument<T>(
                !!persist,
                collectionName,
                updateProps.id,
                appUser,
                orgUser,
                updateProps.updateEvent,
                updateProps.updateContextEvent,
                updateProps.updateErrorEvent,
                enqueueSnackbar,
                'Vellykket!',
                updateProps.updates,
            )
            setSuccess('Vellykket!')
        }
    }

    const notFound = !loading && !saved && !createProps

    const missingOrgId = !!requireOrgId && !orgId

    const content = loading ? <FullscreenLoading screenId={title} /> : (
        missingOrgId ? (
            <div style={{ display: 'grid', placeItems: 'center', height: '60vh' }}>
                <Typography>Venligst <MuiLink href={Paths.SELECT_ORG}>velg en organisasjon</MuiLink> som dokumentet skal forbindes med</Typography>
            </div>
        ) : (
            <>
                {(notFound && altCreateNewProps) ? (
                    (viewMode || !altCreateNewProps.createNewText) ? (
                        <ListItem>
                            <ListItemText primary={notFoundText} />
                        </ListItem>
                    ) : (
                        <ListItem component={Link} to={altCreateNewProps.createNewPath || ''}>
                            <ListItemText primary={notFoundText} secondary={altCreateNewProps.createNewText} />
                        </ListItem>
                    )
                ) : (
                    <>
                        <Snackbar
                            open={!!snacbar}
                            onClose={() => setSnacbar('')}
                        >
                            <Alert severity="error">
                                {snacbar}
                            </Alert>
                        </Snackbar>
                        <Snackbar
                            open={!!success}
                            onClose={() => setSuccess('')}
                        >
                            <Alert severity="success" elevation={6} variant="filled">
                                {success}
                            </Alert>
                        </Snackbar>
                        <List>
                            {!!saved && updateProps?.id ? (
                                saveButtons(updateProps.editProps, updateProps.id, save, singularText, history, listPath, !!updateProps?.updates, appUser, orgUser, lastCreated, canEdit)
                            ) : (
                                createProps ? (
                                    saveButtons(createProps.editProps, createProps.id, save, singularText, history, listPath, !!createProps?.data, appUser, orgUser, lastCreated, canEdit)
                                ) : null
                            )}
                        </List>
                        {children}
                    </>
                )}
            </>
        ))

    return (
        <PageNavigation
            backButton={
                !hideNavigation && (
                    <IconButton color="inherit" aria-label="go back" component={Link} to={listPath}>
                        <ArrowBack />
                    </IconButton>
                )
            }
            title={!saved && createProps ? createProps.createNewTitle : title}
            rightAppBar={(!updateProps?.id || createProps || missingOrgId) ? undefined : (
                <>
                    {notFound ? null : (
                        <>
                            {(!orgUser || !appUser || !saved || !copyProps || !canCreate) ? null : (
                                <IconButton color="inherit" aria-label="copy" onClick={async () => {
                                    setLastCreated('')
                                    const { id, ...rest } = saved
                                    const newId = FirebaseUtils.createDocument<T>(
                                        true,
                                        collectionName,
                                        null,
                                        appUser,
                                        orgUser,
                                        copyProps.createEvent,
                                        copyProps.copyContextEvent,
                                        copyProps.copyErrorEvent,
                                        enqueueSnackbar,
                                        'Vellykket!',
                                        {
                                            ...rest,
                                            ...updateProps.updates,
                                        },
                                    )
                                    await copyProps.onCopy?.(newId)
                                    history.push(listPath + '/' + newId)
                                }}>
                                    <FileCopy />
                                </IconButton>
                            )}
                            {(appUser && deleteProps && canDelete) ? (
                                <IconButton color="inherit" aria-label="delete" onClick={() => {
                                    FirebaseUtils.deleteDocument(
                                        !!persist,
                                        collectionName,
                                        updateProps.id,
                                        appUser,
                                        orgUser,
                                        deleteProps.deleteEvent,
                                        deleteProps.deleteContextEvent,
                                        deleteProps.deleteErrorEvent,
                                        enqueueSnackbar,
                                        'Slettet ' + singularText,
                                        () => ({ appUserId: appUser.id, }),
                                    )
                                    history.push(listPath)
                                }}>
                                    <Delete />
                                </IconButton>
                            ) : null
                            }
                            <Box hidden={!updateProps?.editProps.formIsValid || !!updateProps?.editProps.formIsValid || !updateProps?.updates}>
                                <IconButton color="inherit" aria-label="save" onClick={save}>
                                    <Save />
                                </IconButton>
                            </Box>
                        </>
                    )}
                </>
            )}
        >
            {content}
        </PageNavigation>
    )
}